import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const DIMS = [
  { key: "PHY", name: "Physical Wellness", color: "#173F35" },
  { key: "ENV", name: "Environmental Wellness", color: "#2A5548" },
  { key: "EMO", name: "Emotional Wellness", color: "#B49253" },
  { key: "SPI", name: "Spiritual Wellness", color: "#807B70" },
];

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const fmtFull = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }) + ` · ${fmtTime(iso)}`;

const ChartTip = ({ active, payload, dimName }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-2xl border border-forest/15 bg-ivory px-4 py-3 text-xs shadow-[0_12px_30px_rgba(23,63,53,0.12)]">
      <p className="font-bold text-forest">{p.fullDate}</p>
      <p className="mt-1 text-charcoal/80">
        {dimName}: {p.value == null ? "not enough answers" : `${p.value.toFixed(1)} of 3`}
      </p>
      <p className="mt-0.5 text-taupe">
        Classification: {p.classification || "not enough answers"}
      </p>
    </div>
  );
};

export const ProgressChart = ({ entries }) => {
  const [dim, setDim] = useState("EMO");
  const active = DIMS.find((d) => d.key === dim);

  const dayCounts = entries.reduce((acc, e) => {
    const d = fmtDay(e.created_at);
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  // One plotted point per recorded assessment. No smoothing, no invented values.
  const data = entries.map((e) => {
    const day = fmtDay(e.created_at);
    return {
      id: e.assessment_id,
      date: dayCounts[day] > 1 ? `${day} · ${fmtTime(e.created_at)}` : day,
      fullDate: fmtFull(e.created_at),
      value: e.dimensions?.[dim]?.mean ?? null,
      classification: e.dimensions?.[dim]?.label,
    };
  });

  const first = data[0];
  const last = data[data.length - 1];

  return (
    <div
      data-testid="progress-chart"
      className="rounded-[2rem] border border-forest/10 bg-white/75 p-7 shadow-[0_12px_30px_rgba(23,63,53,0.07)] sm:p-9"
    >
      <h3 className="font-serif text-xl text-forest">How your answers shift over time</h3>
      <p className="mt-2 max-w-xl text-xs leading-relaxed text-taupe">
        Each point is one completed discovery, plotted at its exact recorded value. The line
        shows your self-reported support index (0 to 3): the average of your answers in that
        dimension. It describes what you reported, never a medical measurement.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {DIMS.map((d) => (
          <button
            key={d.key}
            data-testid={`chart-dimension-${d.key.toLowerCase()}`}
            onClick={() => setDim(d.key)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              dim === d.key
                ? "bg-forest text-ivory"
                : "border border-forest/20 text-forest hover:border-gold"
            }`}
          >
            {d.name}
          </button>
        ))}
      </div>
      <div className="mt-6 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 24, right: 16, bottom: 0, left: -14 }}>
            <CartesianGrid stroke="#173F35" strokeOpacity={0.08} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#807B70" }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 3]}
              ticks={[0, 1, 2, 3]}
              tick={{ fontSize: 11, fill: "#807B70" }}
              axisLine={false}
              tickLine={false}
              label={{
                value: "support index",
                angle: -90,
                position: "insideLeft",
                offset: -2,
                style: { fontSize: 10, fill: "#807B70" },
              }}
            />
            <Tooltip content={<ChartTip dimName={active.name} />} />
            <Line
              type="linear"
              dataKey="value"
              stroke={active.color}
              strokeWidth={2.5}
              dot={{ r: 4.5, fill: active.color, strokeWidth: 0 }}
              activeDot={{ r: 6, fill: "#B49253", strokeWidth: 0 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            {first && first.value != null && (
              <ReferenceDot
                x={first.date}
                y={first.value}
                r={7}
                fill="none"
                stroke="#B49253"
                strokeWidth={2}
                label={{
                  value: "Starting Point",
                  position: "top",
                  fontSize: 10,
                  fill: "#7A6A45",
                  fontWeight: 700,
                }}
              />
            )}
            {last && last.value != null && data.length > 1 && (
              <ReferenceDot
                x={last.date}
                y={last.value}
                r={7}
                fill="none"
                stroke="#173F35"
                strokeWidth={2}
                label={{
                  value: "Most Recent Check-In",
                  position: "top",
                  fontSize: 10,
                  fill: "#173F35",
                  fontWeight: 700,
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-4 text-xs text-taupe">
        Inheritance & Personal History is never charted. It remains context for your story, not
        a number. Two discoveries completed on the same day stay separate points.
      </p>
    </div>
  );
};
